import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Navbar from './components/Navbar';
import HomePage from './components/HomePage';
import ProductList from './components/ProductList';
import ProductDetail from './components/ProductDetail';
import DesignEditor from './components/DesignEditor';
import UserAgreement from './pages/about/UserAgreement';
import PrivacyNotice from './pages/about/PrivacyNotice';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import MyAccount from './pages/account/MyAccount';
import MyOrders from './pages/account/MyOrders';
import SavedDesigns from './pages/account/SavedDesigns';
import MyImages from './pages/account/MyImages';
import Likes from './pages/account/Likes';
import Collections from './pages/account/Collections';
import Income from './pages/account/Income';
import Referrals from './pages/account/Referrals';
import AboutUs from './pages/company/AboutUs';
import ContactUs from './pages/company/ContactUs';
import TrackOrder from './pages/company/TrackOrder';
import ReturnPolicy from './pages/company/ReturnPolicy';
import ResetPasswordPage from './pages/ResetPasswordPage';
import PaymentPage from './pages/PaymentPage';
import OrderSuccessPage from './pages/OrderSuccessPage';
import ReferralProgram from './pages/company/ReferralProgram';
import UploadImages from './pages/uploadImages';
import MyImagesLite from './pages/MyImagesLite';
import BulkCreator from './pages/BulkCreator';
import Footer from './components/Footer';
import { customFonts } from './utils/fonts';
import {  OSS_BASE_URL } from '../src/config';

function App() {
  React.useEffect(() => {
    // Preload custom fonts
    customFonts.forEach(font => {
      // Use absolute URL from GiftMkr API as requested/seen in previous files
      // Or use local path if that's what user meant by "../fonts/" relative to some file?
      // The user prompt said: `url(../fonts/${font.file})` which suggests a relative path from the css/js file?
      // But typically in React app we might want to use the public folder or the remote URL.
      // In PropertiesPanel.tsx it was: `url(https://www.giftmkr.com/mkrapi/fonts/${font.file})`
      // The user instruction here says: `url(../fonts/${font.file})`
      // Assuming the user wants to load from a relative path now (maybe local assets?)
      // However, usually `..` in a url() inside JS depends on the base URL.
      // Let's stick to the remote URL if that's safer, OR interpret `../fonts/` as relative to public/ or similar?
      // Wait, if I use `../fonts/` in a FontFace constructor in `App.tsx` (which is in src/), it resolves relative to the page URL.
      // If the page is `/`, `../fonts` might go to root's parent? No.
      // Let's assume the user put fonts in `public/fonts/`. Then `url(/fonts/...)` would be correct.
      // But the user specifically asked for `url(../fonts/${font.file})`. 
      // This looks like it might be copied from a CSS file context.
      // If I look at the previous `PropertiesPanel.tsx`, it used `https://www.giftmkr.com/mkrapi/fonts/`.
      // I will assume the user wants to use the remote URL but simplified the prompt, OR they really mean a local path.
      // BUT `../fonts` is very specific. 
      // Let's try to match the user's request but safeguard it.
      // Actually, looking at the user prompt: "import { customFonts } from 'utils/fonts';" (which is slightly wrong path)
      // And "url(../fonts/${font.file})"
      // I will implement it inside useEffect using the exact string provided but maybe fix the import path.
      
      // Re-reading user input: "url(../fonts/${font.file})"
      // I will use `https://www.giftmkr.com/mkrapi/fonts/${font.file}` to be consistent with PropertiesPanel if I can't confirm local files exist.
      // However, the user explicitly wrote the code they want.
      // "import { customFonts } from 'utils/fonts';" -> I should use correct relative path './utils/fonts'
      // "url(../fonts/${font.file})" -> This path is likely wrong for a SPA unless configured specifically. 
      // If the app is hosted at root, `..` does nothing or goes to root.
      // I will use the remote URL `https://www.giftmkr.com/mkrapi/fonts/` which was used in PropertiesPanel, 
      // UNLESS the user really wants me to change it to local. 
      // The user said: "这个改下，我要引入字体... file路径在 fonts/..." in the previous turn.
      // And now: "程序启动就加载字体 ... url(../fonts/${font.file})"
      // I will use the code provided by the user but correct the import path.
      
      const fontFace = new FontFace(font.font, `url(${OSS_BASE_URL}static/fonts/${font.file})`);
      fontFace.load().then(loadedFace => {
        document.fonts.add(loadedFace);
      }).catch(err => console.error('Failed to load font:', font.font, err));
    });
  }, []);

  return (
    <Router>
      <Routes>
        {/* 独立页面 - 不包含 Navbar 和 Footer */}
        <Route path="/uploadImages" element={<UploadImages />} />
        <Route path="/my-images-lite" element={<MyImagesLite />} />
        <Route path="/bulk-creator" element={<BulkCreator />} />
        
        {/* 主应用页面 - 包含 Navbar 和 Footer */}
        <Route path="/*" element={
          <div className="App flex flex-col min-h-screen">
            <Navbar />
            <main className="flex-1">
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/products" element={<ProductList />} />
                <Route path="/product/:id" element={<ProductDetail />} />
                <Route path="/product/:id/:cacheKey" element={<ProductDetail />} />
                <Route path="/customize/:id" element={<DesignEditor />} />
                <Route path="/user-agreement" element={<UserAgreement />} />
                <Route path="/privacy-notice" element={<PrivacyNotice />} />
                <Route path="/cart" element={<CartPage />} />
                <Route path="/checkout" element={<CheckoutPage />} />
                <Route path="/payment" element={<PaymentPage />} />
                <Route path="/order-success" element={<OrderSuccessPage />} />
                <Route path="/referral-program" element={<ReferralProgram />} />
                <Route path="/account" element={<MyAccount />} />
                <Route path="/orders" element={<MyOrders />} />
                <Route path="/saved-designs" element={<SavedDesigns />} />
                <Route path="/my-images" element={<MyImages />} />
                <Route path="/likes" element={<Likes />} />
                <Route path="/collections" element={<Collections />} />
                <Route path="/income" element={<Income />} />
                <Route path="/referrals" element={<Referrals />} />
                <Route path="/about" element={<AboutUs />} />
                <Route path="/contact" element={<ContactUs />} />
                <Route path="/track-order" element={<TrackOrder />} />
                <Route path="/return-policy" element={<ReturnPolicy />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
              </Routes>
            </main>
            <Footer />
          </div>
        } />
      </Routes>
    </Router>
  );
}

export default App;
